import { useEffect, useRef, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, Button } from '../../../components/ui';
import { useLanguageStore } from '../../../store/languageStore';
import { ROOM_POLLING_INTERVAL_MS } from '../../../utils/polling';
import { getUITranslation, getCardTranslation } from '../i18n/translations';
import { useDelegationPokerStore } from '../store/delegationPokerStore';
import { DelegationCardList } from '../components';
import { getRoom, addSituation, startVoting, submitVote, closeVotingResults, deleteSituation } from '../services/api';
import type { DelegationCard, Situation, VotingSession } from '../types';

export const Room = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { language } = useLanguageStore();
  const t = getUITranslation(language);

  const { cards, roomInfo, setRoomInfo, setCards } = useDelegationPokerStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newSituationDescription, setNewSituationDescription] = useState('');
  const [addingSituation, setAddingSituation] = useState(false);
  const [isAddStoryCollapsed, setIsAddStoryCollapsed] = useState(false);
  const situationInputRef = useRef<HTMLInputElement>(null);

  // Auto-clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
    return;
  }, [error]);
  
  // Voting state
  const [activeVotingSituationId, setActiveVotingSituationId] = useState<string | null>(null);
  const [votingTimeLeft, setVotingTimeLeft] = useState(15);
  const [userVote, setUserVote] = useState<string | null>(null);
  const [votingResults, setVotingResults] = useState<Record<string, number>>({});
  const [votingResultsDetails, setVotingResultsDetails] = useState<Record<string, Array<{userId: string, userName: string}>>>({});
  const [lastVotingSession, setLastVotingSession] = useState<VotingSession | null>(null);
  const [isVotingActive, setIsVotingActive] = useState(false);
  const [isResultsLoading, setIsResultsLoading] = useState(false);
  const [isClosingResults, setIsClosingResults] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [topMessage, setTopMessage] = useState<string | null>(null);

  const userEmail = useMemo(() => sessionStorage.getItem('userEmail'), []);
  const adminToken = useMemo(() => sessionStorage.getItem('adminToken'), []);
  const userToken = useMemo(() => sessionStorage.getItem('userToken'), []);
  const userId = useMemo(() => sessionStorage.getItem('userId'), []);

  const isUserTokenAdmin = (() => {
    if (!userToken) return false;
    try {
      const decodedToken = atob(userToken);
      const tokenParts = decodedToken.split(':');
      return tokenParts[2] === 'true';
    } catch {
      return false;
    }
  })();

  const adminAccessToken = adminToken || (isUserTokenAdmin ? userToken : null);
  const isAdmin = !!adminAccessToken;

  // Fetch room data
  const fetchRoom = async () => {
    if (!roomId) return;

    try {
      const room = await getRoom(roomId);
      setRoomInfo(room);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load room');
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (!userEmail) {
      navigate('/apps/delegation-poker');
      return;
    }

    fetchRoom();
  }, [roomId, userEmail, navigate]);

  // Polling for updates (every 5 seconds)
  useEffect(() => {
    if (!roomId || loading || error) return;

    const pollInterval = ROOM_POLLING_INTERVAL_MS;
    const interval = setInterval(() => {
      fetchRoom();
    }, pollInterval);

    return () => clearInterval(interval);
  }, [roomId, loading, error, isVotingActive]);

  // Initialize cards with translations
  useEffect(() => {
    const translatedCards: DelegationCard[] = [
      { id: 'tell', level: 1, ...getCardTranslation(language, 'tell') },
      { id: 'sell', level: 2, ...getCardTranslation(language, 'sell') },
      { id: 'consult', level: 3, ...getCardTranslation(language, 'consult') },
      { id: 'agree', level: 4, ...getCardTranslation(language, 'agree') },
      { id: 'advise', level: 5, ...getCardTranslation(language, 'advise') },
      { id: 'inquire', level: 6, ...getCardTranslation(language, 'inquire') },
      { id: 'delegate', level: 7, ...getCardTranslation(language, 'delegate') },
    ];
    setCards(translatedCards);
  }, [language, setCards]);

  // Handle add situation
  const handleAddSituation = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!roomId || !newSituationDescription.trim() || !adminAccessToken) {
      return;
    }

    setAddingSituation(true);

    try {
      await addSituation(roomId, {
        description: newSituationDescription,
        adminToken: adminAccessToken,
      });

      // Clear input and refresh room
      setNewSituationDescription('');
      await fetchRoom();
      
      // Scroll to input and return focus for adding next situation
      setTimeout(() => {
        situationInputRef.current?.focus();
        situationInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    } catch (err) {
      console.error('Failed to add situation:', err);
      setError(err instanceof Error ? err.message : 'Failed to add situation');
    } finally {
      setAddingSituation(false);
    }
  };

  // Handle copy room link
  const handleCopyRoomLink = async () => {
    if (!roomId) return;
    
    const joinUrl = `${window.location.origin}/apps/delegation-poker/join?code=${roomId}`;
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle start voting
  const handleStartVoting = async (situationId: string) => {
    if (!roomId || !adminAccessToken) return;

    try {
      setIsAddStoryCollapsed(true);
      // Clear any previous voting results and selection
      setUserVote(null);
      setVotingResults({});
      setVotingResultsDetails({});
      setLastVotingSession(null);
      
      // Start voting on backend
      await startVoting(roomId, { situationId });
      
      // Immediately fetch updated room to sync with server time
      await fetchRoom();
    } catch (err) {
      console.error('Failed to start voting:', err);
      setError(err instanceof Error ? err.message : 'Failed to start voting');
      setIsVotingActive(false);
    }
  };

  // Handle submit vote
  const handleSubmitVote = async (cardId: string) => {
    if (!roomId || !activeVotingSituationId) return;

    try {
      const userName = roomInfo?.participants?.find(p => p.userId === userId)?.name || 'Anonymous';

      const updatedRoom = await submitVote(roomId, {
        situationId: activeVotingSituationId,
        playerId: userId || 'unknown',
        playerName: userName,
        cardId,
      });

      setRoomInfo(updatedRoom);
      setUserVote(cardId);
    } catch (err) {
      console.error('Failed to submit vote:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit vote');
    }
  };

  // Handle close voting results
  const handleCloseResults = async (situationId: string) => {
    if (!roomId || !adminAccessToken || isClosingResults) return;

    try {
      setIsClosingResults(true);
      setTopMessage(null);
      
      // Find the card with most votes BEFORE any state changes
      const maxVotes = Math.max(...Object.values(votingResults));
      const winningCards = Object.entries(votingResults).filter(([_, count]) => count === maxVotes);
      
      // Must have exactly one winning card with votes
      if (winningCards.length !== 1 || maxVotes === 0) {
        setError('Cannot close: no single winning card');
        setIsClosingResults(false);
        return;
      }

      const winningCard = winningCards[0]![0];

      // Reset voting states BEFORE server call  
      setActiveVotingSituationId(null);
      setVotingResults({});
      setVotingResultsDetails({});
      setVotingTimeLeft(15);
      setUserVote(null);
      setLastVotingSession(null);
      setIsVotingActive(false);

      await closeVotingResults(roomId, situationId, winningCard);
      setTopMessage(null);
      
      // Fetch updated room data
      await fetchRoom();
    } catch (err) {
      console.error('Failed to close results:', err);
      setError(err instanceof Error ? err.message : 'Failed to close results');
    } finally {
      setIsClosingResults(false);
    }
  };

  // Handle delete situation
  const handleDeleteSituation = async (situationId: string) => {
    if (!roomId || !adminAccessToken) return;

    if (!confirm('Are you sure you want to delete this situation?')) {
      return;
    }

    try {
      await deleteSituation(roomId, situationId, adminAccessToken);
      await fetchRoom();
    } catch (err) {
      console.error('Failed to delete situation:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete situation');
    }
  };

  const buildResultsFromSession = (session: VotingSession | null | undefined) => {
    const results: Record<string, number> = {};
    const resultsDetails: Record<string, Array<{userId: string, userName: string}>> = {};
    if (!session) {
      return { results, resultsDetails };
    }

    const levelToCard: Record<number, string> = {
      1: 'tell', 2: 'sell', 3: 'consult', 4: 'agree',
      5: 'advise', 6: 'inquire', 7: 'delegate'
    };

    session.votes.forEach(vote => {
      const cardId = levelToCard[vote.level];
      if (cardId) {
        results[cardId] = (results[cardId] || 0) + 1;

        if (!resultsDetails[cardId]) {
          resultsDetails[cardId] = [];
        }
        resultsDetails[cardId].push({
          userId: vote.userId,
          userName: vote.userName || 'Anonymous'
        });
      }
    });

    return { results, resultsDetails };
  };

  // Sync voting state with backend data (for all participants)
  useEffect(() => {
    if (!roomInfo?.currentVotingSession) {
      // No active voting session in backend
      // Don't clear activeVotingSituationId here - keep it to show results
      // Only clear when user manually closes results
      if (lastVotingSession && !activeVotingSituationId) {
        setActiveVotingSituationId(lastVotingSession.situationId);
      }

      if (lastVotingSession && Object.keys(votingResults).length === 0) {
        const results: Record<string, number> = {};
        const resultsDetails: Record<string, Array<{userId: string, userName: string}>> = {};
        const levelToCard: Record<number, string> = {
          1: 'tell', 2: 'sell', 3: 'consult', 4: 'agree',
          5: 'advise', 6: 'inquire', 7: 'delegate'
        };

        lastVotingSession.votes.forEach(vote => {
          const cardId = levelToCard[vote.level];
          if (cardId) {
            results[cardId] = (results[cardId] || 0) + 1;

            if (!resultsDetails[cardId]) {
              resultsDetails[cardId] = [];
            }
            resultsDetails[cardId].push({
              userId: vote.userId,
              userName: vote.userName || 'Anonymous'
            });
          }
        });

        setVotingResults(results);
        setVotingResultsDetails(resultsDetails);
      }

      if (isVotingActive) {
        setIsVotingActive(false);
        // Don't clear activeVotingSituationId - we need it to show results
      }
      return;
    }

    const session = roomInfo.currentVotingSession;
    const now = Date.now();
    const timeLeft = Math.max(0, Math.floor((session.endsAt - now) / 1000));



    // Update voting states based on backend data
    if (session.isActive && timeLeft > 0) {
      setActiveVotingSituationId(session.situationId);
      setIsVotingActive(true);
      setVotingTimeLeft(timeLeft);
      setLastVotingSession(null);
      
      // Check if current user already voted
      if (userId && session.votes.some(v => v.userId === userId)) {
        const userVoteData = session.votes.find(v => v.userId === userId);
        if (userVoteData) {
          // Map level back to cardId
          const levelToCard: Record<number, string> = {
            1: 'tell', 2: 'sell', 3: 'consult', 4: 'agree',
            5: 'advise', 6: 'inquire', 7: 'delegate'
          };
          setUserVote(levelToCard[userVoteData.level] || null);
        }
      }
    } else if (!session.isActive || timeLeft <= 0) {
      // Voting session ended - calculate and show results
      setActiveVotingSituationId(session.situationId);
      setIsVotingActive(false);
      setLastVotingSession(session);
      
      const { results, resultsDetails } = buildResultsFromSession(session);
      setVotingResults(results);
      setVotingResultsDetails(resultsDetails);
    }
  }, [roomInfo?.currentVotingSession, userId, lastVotingSession]);

  useEffect(() => {
    if (!roomInfo?.currentVotingSession) return;
    if (!isVotingActive && votingTimeLeft === 0) {
      const { results, resultsDetails } = buildResultsFromSession(roomInfo.currentVotingSession);
      setVotingResults(results);
      setVotingResultsDetails(resultsDetails);
      setLastVotingSession(roomInfo.currentVotingSession);
    }
  }, [isVotingActive, votingTimeLeft, roomInfo?.currentVotingSession]);

  // Timer effect for voting countdown
  useEffect(() => {
    if (!isVotingActive || votingTimeLeft <= 0) {
      if (votingTimeLeft <= 0 && isVotingActive) {
        setIsVotingActive(false);
        setIsResultsLoading(true);
        setTimeout(() => {
          fetchRoom().finally(() => setIsResultsLoading(false));
        }, 2000);
      }
      return;
    }

    const timer = setTimeout(() => {
      setVotingTimeLeft(votingTimeLeft - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isVotingActive, votingTimeLeft]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <p className="text-lg text-muted-foreground">Loading room...</p>
        </div>
      </div>
    );
  }

  if (!roomInfo) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <p className="mb-4 text-lg text-destructive">Room not found</p>
          <button
            onClick={() => navigate('/apps/delegation-poker')}
            className="text-primary hover:underline"
          >
            Go back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Floating Error Notification */}
      {error && (
        <div className="fixed top-8 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-top-5 duration-300">
          <div className="bg-destructive text-destructive-foreground px-6 py-4 rounded-lg shadow-2xl border-2 border-destructive/50 max-w-md">
            <p className="font-semibold text-center text-lg">⚠️ {error}</p>
          </div>
        </div>
      )}
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="mb-4 text-5xl font-bold text-center text-primary">Delegation Poker</h1>
          <h2 className="mb-4 text-3xl font-semibold">{roomInfo.name}</h2>
          
          {/* Room Code with Copy Button */}
          <div className="mb-4 flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Room Code:</span>
            <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-1">
              <code className="font-mono text-sm font-semibold">{roomId}</code>
              <button
                onClick={handleCopyRoomLink}
                className="ml-2 inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 cursor-pointer"
                title="Copy join link"
              >
                📋
                {copyFeedback && <span className="text-xs">Copied!</span>}
              </button>
            </div>
          </div>
        </div>

        {/* Players List */}
        <Card className="mb-8">
          <CardContent className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-semibold">{t.room.players}</h2>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-muted-foreground">Total: {roomInfo.participants?.length || 0}</span>
                {isAdmin && <span className="text-primary">• Admin</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {roomInfo.participants?.map((participant) => (
                <div
                  key={participant.userId}
                  className="rounded-md bg-muted px-3 py-1 text-sm"
                >
                  {participant.name}
                  {participant.isAdmin && (
                    <span className="ml-1 text-xs text-primary">★</span>
                  )}
                </div>
              ))}

              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>
                  {t.room.players}: {roomInfo.participants?.length || 0}
                </span>
                {isAdmin && <span className="text-primary">• Admin</span>}
              </div>
            </div>
          </CardContent>
        </Card>

        {topMessage && (
          <div className="shrink-0 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {topMessage}
          </div>
        )}

        <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-12">
          <div className="min-h-0 lg:col-span-6">
            <Card className="h-full">
              <CardContent className="flex h-full min-h-0 flex-col p-3 lg:p-4">
                {isAdmin && (
                  <div
                    className={`shrink-0 rounded-lg border border-primary/40 bg-primary/5 ${
                      isAddStoryCollapsed ? 'p-2' : 'p-4'
                    }`}
                  >
                    <div
                      className={`flex items-center justify-between gap-2 ${
                        isAddStoryCollapsed ? 'mb-0' : 'mb-2'
                      }`}
                    >
                      <h2 className="text-base font-semibold">✎ Add Situation</h2>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setIsAddStoryCollapsed((prev) => !prev)}
                        aria-label={isAddStoryCollapsed ? 'Expand add situation panel' : 'Minimize add situation panel'}
                        title={isAddStoryCollapsed ? 'Expand' : 'Minimize'}
                        className="h-8 w-8 p-0"
                      >
                        <span aria-hidden="true">{isAddStoryCollapsed ? '▾' : '▴'}</span>
                      </Button>
                    </div>

                    {!isAddStoryCollapsed && (
                      <form onSubmit={handleAddSituation} className="space-y-2">
                        <div>
                          <input
                            ref={situationInputRef}
                            type="text"
                            value={newSituationDescription}
                            onChange={(e) => setNewSituationDescription(e.target.value)}
                            placeholder="Describe a delegation situation..."
                            disabled={addingSituation}
                            className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                            maxLength={200}
                          />
                          <p className="mt-1 text-xs text-muted-foreground">
                            {newSituationDescription.length}/200
                          </p>
                        </div>
                        <Button
                          type="submit"
                          size="sm"
                          disabled={addingSituation || !newSituationDescription.trim()}
                          className="w-full"
                        >
                          {addingSituation ? 'Adding...' : 'Add Situation'}
                        </Button>
                      </form>
                    )}
                  </div>
                )}

                <div className="mt-2 min-h-0 flex-1 overflow-y-auto pr-1">
                  <h2 className="mb-2 text-base font-semibold">{t.room.situations}</h2>
                  {roomInfo.situations && roomInfo.situations.length > 0 ? (
                    <div className="space-y-3">
                      {roomInfo.situations.map((situation: Situation) => (
                        <div
                          key={situation.id}
                          className={`rounded border p-2 ${
                            situation.finalChoice ? 'border-green-500 bg-green-50' : 'border-border'
                          }`}
                        >
                          <div className="mb-2 flex flex-col gap-1.5 lg:flex-row lg:items-center lg:justify-between">
                            <p className="text-sm font-medium">
                              {situation.description}
                              {situation.finalChoice && (
                                <span className="ml-2 text-xs font-semibold text-green-600">
                                  ✓ Final Choice: {cards.find((c) => c.id === situation.finalChoice)?.name}
                                </span>
                              )}
                            </p>
                            <div className="flex gap-2">
                              {isAdmin && !situation.finalChoice && !isVotingActive && activeVotingSituationId !== situation.id && (
                                <Button size="sm" onClick={() => handleStartVoting(situation.id)}>
                                  Start
                                </Button>
                              )}
                              {isAdmin && !isVotingActive && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteSituation(situation.id)}
                                >
                                  Delete
                                </Button>
                              )}
                            </div>
                          </div>

                          {activeVotingSituationId === situation.id && isVotingActive && (
                            <div className="mb-2 rounded-lg bg-primary/10 p-2 text-center">
                              <div className="text-2xl font-bold text-primary">{votingTimeLeft}s</div>
                              <p className="text-xs text-muted-foreground">Voting in progress... You can change your vote anytime!</p>
                            </div>
                          )}

                          {activeVotingSituationId === situation.id && !isVotingActive && !situation.finalChoice && (Object.keys(votingResults).length > 0 || lastVotingSession?.situationId === situation.id) && (
                            <div className="mb-1">
                              <h4 className="mb-2 font-semibold">Voting Results:</h4>
                              {isResultsLoading && (
                                <div className="mb-3 rounded border border-border bg-muted p-3 text-sm text-muted-foreground">
                                  Calculating results...
                                </div>
                              )}
                              {(() => {
                                const fallbackSession = lastVotingSession?.situationId === situation.id
                                  ? lastVotingSession
                                  : roomInfo.currentVotingSession;
                                const derived = Object.keys(votingResults).length > 0
                                  ? { results: votingResults, resultsDetails: votingResultsDetails }
                                  : buildResultsFromSession(fallbackSession);

                                const results = situation.voteResults || derived.results;
                                const resultsDetails = situation.voteDetails || derived.resultsDetails;
                                const maxVotes = Math.max(0, ...Object.values(results));

                                return (
                                  <>
                                    {isAdmin ? (
                                      <div className="grid w-full grid-cols-2 gap-2 xl:grid-cols-4">
                                        {cards.map((card) => {
                                          const cardVoters = resultsDetails[card.id] || [];
                                          const isWinning = results[card.id] === maxVotes && maxVotes > 0;

                                          return (
                                            <div
                                              key={card.id}
                                              className={`rounded border p-2 ${
                                                isWinning
                                                  ? 'border-green-500 bg-green-50'
                                                  : 'border-border bg-muted'
                                              }`}
                                            >
                                              <div className="mb-1 flex items-center justify-between">
                                                <p className="text-xs font-semibold">
                                                  {card.name} {isWinning && '🏆'}
                                                </p>
                                                <span className="text-xs font-bold text-primary">
                                                  {cardVoters.length}
                                                </span>
                                              </div>
                                              {cardVoters.length > 0 && (
                                                <div className="flex flex-wrap gap-1">
                                                  {cardVoters.map((voter, idx) => (
                                                    <span
                                                      key={idx}
                                                      className="rounded bg-background px-1.5 py-0.5 text-[10px]"
                                                    >
                                                      {voter.userName}
                                                    </span>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <div className="flex flex-wrap gap-2">
                                        {cards.map((card) => {
                                          const votes = results[card.id] || 0;
                                          const isWinning = votes === maxVotes && maxVotes > 0;

                                          return (
                                            <div key={card.id} className="text-center">
                                              <div
                                                className={`rounded border p-2 ${
                                                  isWinning
                                                    ? 'border-green-500 bg-green-100'
                                                    : 'border-border bg-muted'
                                                }`}
                                              >
                                                <p className="text-xs font-bold text-primary">
                                                  {votes} {isWinning && '🏆'}
                                                </p>
                                                <p className="text-[10px] text-muted-foreground">{card.name}</p>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {isAdmin && (
                                      <div className="mt-3 flex gap-2">
                                        <Button
                                          size="sm"
                                          onClick={() => handleCloseResults(situation.id)}
                                          disabled={isClosingResults}
                                          className="flex-1 bg-green-600 hover:bg-green-700"
                                        >
                                          {isClosingResults ? 'Closing...' : 'Close Results'}
                                        </Button>
                                        <Button
                                          size="sm"
                                          onClick={() => handleStartVoting(situation.id)}
                                          variant="outline"
                                          className="flex-1"
                                        >
                                          Vote Again
                                        </Button>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No situations yet.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <aside className="min-h-0 overflow-hidden lg:col-span-6">
            <div className="flex h-full min-h-0 flex-col gap-3">
              <Card className={`min-h-0 flex-1 ${isVotingActive ? 'border-primary/50 bg-primary/5' : ''}`}>
                <CardContent className="flex h-full min-h-0 flex-col px-4 pb-4 pt-6">
                  {isVotingActive && activeVotingSituationId ? (
                    <>
                      <h3 className="mb-2 text-center text-base font-semibold">🗳️ Vote Now!</h3>
                      <p className="mb-3 text-center text-xs text-muted-foreground">
                        Click on a card to vote. You can change your vote anytime within {votingTimeLeft}s!
                      </p>
                      <div className="min-h-0 flex-1 overflow-hidden">
                        <DelegationCardList
                          cards={cards}
                          selectedCardId={userVote || undefined}
                          votedCardId={userVote || undefined}
                          disabled={false}
                          onCardClick={handleSubmitVote}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="mb-2 text-center text-base font-semibold">Cards</h3>
                      <div className="min-h-0 flex-1 overflow-hidden">
                        <DelegationCardList
                          cards={cards}
                          selectedCardId={undefined}
                          votedCardId={undefined}
                          disabled={false}
                          onCardClick={() => {}}
                        />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};
